import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import { Box, Rating, Text, Meter, Level, Button, MediaQuery } from '@deseretbook/react-ui'
import { Link } from '../../core'
import { ClickableContent } from '../MainContent'
import { Analytics } from '../../../dbcom'

/**
 * This is the Review Overview Component
 *
 * @returns {func} Component
 */
export const ReviewsOverview = ({
  productId,
  avgRating,
  reviews = [],
  setFilters,
}) => {
  if (!productId) {
    throw new Error('Product ID is undefined')
  }
  /**
   * Formats averageRating to a single decimal rating.
   */
  const averageRating = Number(avgRating || 0).toFixed(1)

  /**
   * Checks if the Current User has written a Review for the product.
   * Returns true if the user has written a review
   */
  const userHasReview = reviews.find(review => review.userId === Number(window.__USER__.id))

  /**
   * Groups Reviews by ratings
   */
  const reviewsByRating = reviews.reduce((groups, review) => {
    groups[review.rating].push(review)

    return groups
  }, {
    1: [],
    2: [],
    3: [],
    4: [],
    5: [],
  })

  /**
   * Sets the width for the review count within the review summary.
   * if there are over 99 reviews the width needs to be larger in
   * order to look good. Otherwise the width will be smaller to avoid
   * having too much white space.
   */
  const countWidth = reviews.length > 99
    ? 3
    : 2

  /**
   * Updates Filter array by rating
   * @param {integer} rating - value to be added/removed from
   * the filters state.
   */
  const handleRatingChange = (rating) => {
    setFilters(currentFilters => ({ ...currentFilters, rating: Number(rating) }))
    Analytics.track({
      category: 'Reviews',
      action: 'Click reviews overview',
      label: `Filter reviews by ${rating} stars`,
      value: Number(rating),
    }, { isInteractive: true })
  }


  return (
    <MediaQuery.Medium>
      {isMediumOrLarger => (
        <Fragment>
          <Level>
            <Level.Left isShrinkable>
              <Level.Item>
                <Box
                  {
                    ...isMediumOrLarger
                      ? { width: 14, alignItems: 'start', isColumn: true }
                      : { width: 12 }
                  }
                >
                  <Box paddingBottom={1} alignItems="center" isColumn={!isMediumOrLarger}>
                    <Text isBold size="xLarge">{averageRating}</Text>
                    <Box
                      isColumn
                      alignItems={isMediumOrLarger
                        ? 'start'
                        : 'center'
                      }
                      marginLeft={isMediumOrLarger && 0.5}
                    >
                      { isMediumOrLarger && <Rating average={averageRating} /> }
                      <Text color="disabled" size="small">{reviews.length} Reviews</Text>
                    </Box>
                  </Box>
                  {(isMediumOrLarger && !userHasReview) && (
                    <Button href={`/products/${productId}/reviews/new`} asComponent={Link}>
                      {
                        reviews.length === 0
                        ? 'Be First to Review'
                        : 'Write a Review'
                      }
                    </Button>
                  )}
                </Box>
              </Level.Item>
            </Level.Left>
            <Level.Right isShrinkable>
              <Level.Item>
                <Box flexGrow={1} width={20}>
                  {
                    [5, 4, 3, 2, 1].map(starRating => (
                      <ClickableContent
                        onClick={() => handleRatingChange(starRating)}
                        isPaddingless
                        key={starRating}
                      >
                        <Level>
                          <Level.Left>
                            <Level.Item>
                              <Box width={4} >
                                <Rating average={starRating} type="minimal" />
                              </Box>
                            </Level.Item>
                          </Level.Left>
                          <Level.Item>
                            <Meter min={0} value={(reviewsByRating[starRating].length / reviews.length) * 100} max={100} title="Reviews" />
                          </Level.Item>
                          <Level.Right>
                            <Level.Item>
                              <Box width={countWidth}>
                                <Text hasEllipsis alignment="right">{reviewsByRating[starRating].length}</Text>
                              </Box>
                            </Level.Item>
                          </Level.Right>
                        </Level>
                      </ClickableContent>
                    ))
                  }
                </Box>
              </Level.Item>
            </Level.Right>
          </Level>
          {(!isMediumOrLarger && !userHasReview) && (
            <Box
              paddingVertical={2}
              alignItems="center"
              justifyContent={isMediumOrLarger
                ? 'start'
                : 'center'
              }
            >
              <Button href={`/products/${productId}/reviews/new`} asComponent={Link}>
                {
                  reviews.length === 0
                  ? 'Be the First to Review'
                  : 'Write a Review'
                }
              </Button>
            </Box>
          )}
        </Fragment>
      )}
    </MediaQuery.Medium>
  )
}

ReviewsOverview.defaultProps = {
  reviews: [],
}

ReviewsOverview.propTypes = {
  productId: PropTypes.number.isRequired,
  avgRating: PropTypes.string.isRequired,
  reviews: PropTypes.arrayOf(PropTypes.shape({
    rating: PropTypes.number,
    userId: PropTypes.number,
    review: PropTypes.string,
    title: PropTypes.string,
    createdAt: PropTypes.date,
  })),
  setFilters: PropTypes.func,
}
